import React from "react";
import { Box, Grid } from "@mui/material";
import Input from "../../../../../../components/Input";
import Text from "../../../../../../components/Text";
import dayjs from "dayjs";
import CustomDatePicker from "../../../../../../components/CustomDatePicker";
import AddressCommon from "../../../../../../components/app/provider/patient/add-new-patient/preferences/AddressCommon";
import { STATE_STRING_LIST, TIME_ZONE_LIST } from "../../../../../../constants";
import CustomSelect from "../../../../../../components/CustomSelect";
// GENDER fed the removed Sex / Gender Identity / Sexual Orientation fields.
// import { GENDER } from "../../../../../../constants/master";
import {
  ENTHNICITY_LIST,
  RACE_LIST,
  YES_NO_LIST,
} from "../../../../../../dummy-data/add-new-patient";
import MaskMultiSelect from "../../../../../../components/MaskMultiSelect";
import { getXtenantId } from "../../../../../../utils/utils";
import { useLanguageList } from "../../../../../../queries/use-language-list";
import { getPublicRealm } from "../../../../../../layout/app/patient-portal/public-view/dob-validation/useValidateDob";
import { MARITAL_STATUS } from "../../../../../../constants/provider/add-new-patient";
import { DefaultBreakpointGrid } from "../IntakeBasicDetails";

interface ClientInformationFormProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
}

const ClientInformationForm = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched,
  errors,
  viewOnly,
}: ClientInformationFormProps) => {
  const { data: languageList } = useLanguageList({
    xTENANTID: getXtenantId() ?? getPublicRealm() ?? undefined,
  });

  return (
    <Box>
      <Text variant="subtitle1" fontWeight="500" mb={1}>
        Client Information Form
      </Text>
      <Grid container spacing={2}>
        {/* Legal Name Row */}
        <DefaultBreakpointGrid>
          <Input
            label="Legal Name:"
            textFieldProps={{
              name: "firstName",
              value: values?.firstName,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "First Name",
              disabled: viewOnly,
            }}
            required
            error={touched?.firstName && (errors as any)?.firstName}
          />
        </DefaultBreakpointGrid>
        <DefaultBreakpointGrid>
          <Input
            label="Middle Name"
            textFieldProps={{
              name: "middleName",
              value: values?.middleName,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "middle",
              disabled: viewOnly,
            }}
          />
        </DefaultBreakpointGrid>
        <DefaultBreakpointGrid>
          <Input
            label="Last Name"
            textFieldProps={{
              name: "lastName",
              value: values?.lastName,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Last Name",
              disabled: viewOnly,
            }}
          />
        </DefaultBreakpointGrid>
        {/* Suffix removed per BA approval — kept for discoverability.
        <DefaultBreakpointGrid>
          <Input
            label="Suffix"
            textFieldProps={{
              name: "suffix",
              value: values?.suffix,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "suffix",
              disabled: viewOnly,
            }}
          />
        </DefaultBreakpointGrid>
        */}

        {/* Pronouns */}
        {/* Preferred Name removed per BA approval — kept for discoverability.
        <DefaultBreakpointGrid>
          <Input
            label="Preferred Name:"
            textFieldProps={{
              name: "preferredName",
              value: values?.preferredName,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "optional",
              disabled: viewOnly,
            }}
          />
        </DefaultBreakpointGrid>
        */}
        <DefaultBreakpointGrid>
          <Input
            label="Pronouns:"
            textFieldProps={{
              name: "pronouns",
              value: values?.pronouns || "",
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Selection Text",
              disabled: viewOnly,
            }}
          />
        </DefaultBreakpointGrid>

        {/* Date of Birth */}
        <DefaultBreakpointGrid>
          <CustomDatePicker
            disabled={viewOnly}
            label="Date Of Birth"
            name="birthDate"
            maxDate={dayjs(new Date())}
            placeholder="Choose Date"
            handleBlur={handleBlur}
            value={values.birthDate ? dayjs(values.birthDate) : null}
            onChange={(value) => setFieldValue("birthDate", value)}
          />
        </DefaultBreakpointGrid>

        {/* Address Blocks : note - we are using same address format all over application. approved by BA */}
        <AddressCommon
          formik={{ values, setFieldValue, handleBlur, handleChange } as any}
          formikKey={`address`}
          disabled={viewOnly}
          stateList={STATE_STRING_LIST}
        />

        {/* Time Zone */}
        <Grid item xs={12}>
          <CustomSelect
            label="Time Zone"
            name="timezone"
            value={values.timezone}
            options={TIME_ZONE_LIST}
            onChange={(val) => setFieldValue("timezone", val)}
            disablePortal
            typographyProps={{
              onBlur: values.handleBlur,
            }}
          />
        </Grid>

        {/* Phone Numbers */}
        {["Mobile Phone:", "Home Phone:", "Work Phone:"].map((phoneLabel) => {
          const fieldKey = phoneLabel.toLowerCase().replace(" phone:", "");
          return (
            <DefaultBreakpointGrid key={phoneLabel}>
              <Input
                label={phoneLabel}
                textFieldProps={{
                  name: `${fieldKey}Phone`,
                  value: values?.[`${fieldKey}Phone`] || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                  placeholder: "Selection Text",
                  disabled: viewOnly,
                }}
              />
            </DefaultBreakpointGrid>
          );
        })}

        {/* Administrative Sex, Gender Identity and Sexual Orientation removed
            per BA approval — kept for discoverability.
        <DefaultBreakpointGrid>
          <CustomSelect
            readOnly={viewOnly}
            value={values.sex}
            onChange={(value) => setFieldValue("sex", value)}
            label="Sex"
            clearable
            placeholder="Select Sex"
            options={GENDER}
          />
        </DefaultBreakpointGrid>
        <DefaultBreakpointGrid>
          <CustomSelect
            readOnly={viewOnly}
            value={values.genderIdentity}
            onChange={(value) => setFieldValue("genderIdentity", value)}
            label="Gender Identity"
            clearable
            placeholder="Select Gender Identity"
            options={GENDER}
          />
        </DefaultBreakpointGrid>
        <DefaultBreakpointGrid>
          <CustomSelect
            readOnly={viewOnly}
            value={values.sexualOrientation}
            onChange={(value) => setFieldValue("sexualOrientation", value)}
            label="Sexual Orientation"
            clearable
            placeholder="Select Sexual Orientation"
            options={GENDER}
          />
        </DefaultBreakpointGrid>
        */}
        <DefaultBreakpointGrid>
          <CustomSelect
            value={values.race}
            readOnly={viewOnly}
            clearable
            onChange={(value) => setFieldValue("race", value)}
            label="Race"
            placeholder="Select Race"
            options={RACE_LIST}
          />
        </DefaultBreakpointGrid>
        <DefaultBreakpointGrid>
          <CustomSelect
            readOnly={viewOnly}
            value={values.Ethnicity}
            onChange={(value) => setFieldValue("Ethnicity", value)}
            label="Ethnicity"
            clearable
            placeholder="Select Ethnicity"
            options={ENTHNICITY_LIST}
          />
        </DefaultBreakpointGrid>
        <DefaultBreakpointGrid>
          <MaskMultiSelect
            label="Languages"
            options={languageList?.data?.content}
            optionWithCheckbox={true}
            value={(values as any)?.languages}
            onChange={(values) => {
              setFieldValue("languages", values);
            }}
          />
        </DefaultBreakpointGrid>
        {/* Smoking Status removed per BA approval — kept for discoverability.
        <DefaultBreakpointGrid>
          <CustomSelect
            value={values.smokingStatus}
            readOnly={viewOnly}
            clearable
            onChange={(value) => setFieldValue("smokingStatus", value)}
            label="Smoking Status"
            placeholder="Select Smoking Status"
            options={YES_NO_LIST}
          />
        </DefaultBreakpointGrid>
        */}
        <DefaultBreakpointGrid>
          <CustomSelect
            value={values.maritalStatus}
            readOnly={viewOnly}
            clearable
            onChange={(value) => setFieldValue("maritalStatus", value)}
            label="Marital Status"
            placeholder="Select Marital Status"
            options={MARITAL_STATUS}
          />
        </DefaultBreakpointGrid>
        <DefaultBreakpointGrid>
          <CustomSelect
            value={values.employment}
            readOnly={viewOnly}
            clearable
            onChange={(value) => setFieldValue("employment", value)}
            label="Employment"
            placeholder="Select Employment"
            options={YES_NO_LIST}
          />
        </DefaultBreakpointGrid>
        {/* Religious Affiliation removed per BA approval — kept for discoverability.
        <DefaultBreakpointGrid>
          <CustomSelect
            value={values.religiousAffiliation}
            readOnly={viewOnly}
            clearable
            onChange={(value) => setFieldValue("religiousAffiliation", value)}
            label="Religious Affiliation"
            placeholder="Select Religious Affiliation"
            options={YES_NO_LIST}
          />
        </DefaultBreakpointGrid>
        */}
      </Grid>
    </Box>
  );
};

export default ClientInformationForm;
